import React from 'react';
import { Document } from '../../../gql/graphql';
import { DocumentIcon, FolderIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { formatDate, formatFileSize } from '../../../utils/file_util';
import { useAdminTranslation } from '../../../modules/i18n';

interface FolderTreeItem {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  parentId?: string;
  userId: string;
  subFolders?: FolderTreeItem[];
  documents?: Document[];
  _count?: {
    documents: number;
    subFolders: number;
  } | null;
}

interface FolderTreeViewProps {
  folderTree: FolderTreeItem[];
  openFolders: Record<string, boolean>;
  selectedDocuments: string[];
  selectedFolders: string[];
  onToggleFolder: (folderId: string) => void;
  onFolderSelection: (id: string, checked: boolean) => void;
  onFolderDelete: (folderIds: string[]) => void;
  setCurrentRecord: (record: Document) => void;
  showRenameModal: () => void;
  showMoveFileModal: () => void;
  handleRemoveFile: (recordIds: string[]) => void;
  deletingDocumentIds: string[];
  deletingFolderIds: string[];
  onDocumentSelection?: (id: string, checked: boolean) => void;
  docIdsInsideSelectedFolders?: string[];
  searchTerm?: string;
  sortDirection?: 'asc' | 'desc';
  isFolderLoading?: (folderId: string) => boolean;
  onFolderHover?: (folderId: string) => void;
}

const FolderTreeView: React.FC<FolderTreeViewProps> = ({
  folderTree,
  openFolders,
  selectedDocuments,
  selectedFolders,
  onToggleFolder,
  onFolderSelection,
  onFolderDelete,
  setCurrentRecord,
  showRenameModal,
  showMoveFileModal: _showMoveFileModal,
  handleRemoveFile,
  deletingDocumentIds,
  deletingFolderIds,
  onDocumentSelection,
  docIdsInsideSelectedFolders,
  searchTerm,
  sortDirection = 'asc',
  isFolderLoading,
  onFolderHover
}) => {
  const { t } = useAdminTranslation();
  // Accent-insensitive highlight with correct indices using grapheme segmentation
  const normalize = (s: string) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'd')
      .replace(/\s+/g, ' ');

  const findAccentInsensitiveRange = (text: string, term: string): { start: number; end: number } | null => {
    const q = (term || '').trim();
    if (!q) return null;

    try {
      const segmenter = new Intl.Segmenter('vi', { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text));
      const normSegs = segments.map((seg) => normalize(seg.segment));
      const normJoined = normSegs.join('');
      const normTerm = normalize(q);
      const idx = normJoined.indexOf(normTerm);
      if (idx === -1) return null;

      // Map normalized indices back to segment indices
      const startNormIdx = idx;
      const endNormIdx = idx + normTerm.length - 1;

      let startSegIdx = 0;
      let endSegIdx = 0;
      let cursor = 0;
      for (let i = 0; i < normSegs.length; i++) {
        const segLen = normSegs[i].length;
        const segStart = cursor;
        const segEnd = cursor + segLen - 1;
        if (startSegIdx === 0 && startNormIdx >= segStart && startNormIdx <= segEnd) startSegIdx = i;
        if (endNormIdx >= segStart && endNormIdx <= segEnd) {
          endSegIdx = i;
          break;
        }
        cursor += segLen;
      }

      const start = segments[startSegIdx].index as number;
      const end = (segments[endSegIdx].index as number) + segments[endSegIdx].segment.length;
      return { start, end };
    } catch {
      // Fallback: may produce misaligned indices in very old browsers
      const normText = normalize(text);
      const normTerm = normalize(q);
      const idx = normText.indexOf(normTerm);
      if (idx === -1) return null;
      return { start: idx, end: idx + q.length };
    }
  };

  const highlight = (text: string) => {
    const range = findAccentInsensitiveRange(text, searchTerm || '');
    if (!range) return text;
    const before = text.slice(0, range.start);
    const match = text.slice(range.start, range.end);
    const after = text.slice(range.end);
    return (
      <span>
        {before}
        <span className='bg-yellow-200 text-gray-900 dark:bg-yellow-600 dark:text-yellow-100'>{match}</span>
        {after}
      </span>
    );
  };
  const compareByName = (a: string, b: string) => a.localeCompare(b, 'vi', { sensitivity: 'base' });
  const sortFactor = sortDirection === 'asc' ? 1 : -1;
  const getExtension = (name: string) => {
    const extension = name?.split('.')?.[name.split('.').length - 1]?.toLowerCase() ?? '';
    return extension === '' ? 'folder' : extension;
  };

  const getTotalFilesCount = (folder: FolderTreeItem): number => {
    let count = folder._count?.documents ?? 0;
    if (folder.subFolders) {
      folder.subFolders.forEach((subfolder) => {
        count += getTotalFilesCount(subfolder);
      });
    }
    return count;
  };

  const renderFolderContent = (folder: FolderTreeItem, depth = 0) => {
    const totalFiles = getTotalFilesCount(folder);
    const sortedSubFolders = [...(folder.subFolders || [])].sort((a, b) => sortFactor * compareByName(a.name, b.name));
    const sortedDocuments = [...(folder.documents || [])].sort((a, b) => sortFactor * compareByName(a.title, b.title));
    const isLoading = isFolderLoading ? isFolderLoading(folder.id) : false;

    return (
      <React.Fragment key={folder.id}>
        {/* Folder Row */}
        <tr className='group hover:bg-gray-50 dark:hover:bg-dark-background-hover' onMouseEnter={() => onFolderHover && onFolderHover(folder.id)}>
          <td className='overflow-hidden px-6 py-4'>
            <div className='flex min-w-0 items-center'>
              <div
                style={{ marginLeft: folder.parentId ? `${(depth + 1) * 1.5}rem` : `${depth * 1.5}rem` }}
                className='flex min-w-0 flex-1 items-center'
              >
                {folder.parentId === null && (
                  <input
                    type='checkbox'
                    checked={selectedFolders.includes(folder.id)}
                    onChange={(e) => onFolderSelection(folder.id, e.target.checked)}
                    className='mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-border-dark'
                  />
                )}
                <button onClick={() => onToggleFolder(folder.id)} className='mr-2 flex-shrink-0 rounded p-1 hover:bg-gray-200 dark:hover:bg-dark-background-hover'>
                  <svg
                    className={`h-4 w-4 text-gray-600 transition-transform dark:text-dark-text-secondary ${openFolders[folder.id] ? 'rotate-90 transform' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                  </svg>
                </button>
                <FolderIcon className='mr-2 h-6 w-6 flex-shrink-0 text-yellow-500' />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <div className='truncate text-sm font-medium text-gray-900 dark:text-dark-text'>{highlight(folder.name) as any}</div>
                    {isLoading && (
                      <svg className='h-4 w-4 animate-spin text-blue-500' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                    )}
                  </div>
                  <div className='text-sm text-gray-500 dark:text-dark-text-secondary'>{isLoading ? 'Loading documents...' : `${totalFiles} files`}</div>
                </div>
              </div>
            </div>
          </td>
          <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-dark-text-secondary'>{formatDate(folder.createdAt)}</td>
          <td className='whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-dark-text-secondary'>-</td>
          <td className='whitespace-nowrap px-4 py-4'>
            <span className='inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200'>
              {t('content.documents.folderTree.folder')}
            </span>
          </td>
          <td className='whitespace-nowrap px-4 py-4 text-right text-sm font-medium'>
            <div className='flex items-center justify-end space-x-2'>
              <button
                onClick={() => onFolderDelete([folder.id])}
                className='text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:text-red-300'
                disabled={deletingFolderIds.includes(folder.id)}
                title={t('content.documents.folderTree.actions.deleteFolder')}
              >
                {deletingFolderIds.includes(folder.id) ? (
                  <svg className='h-5 w-5 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                ) : (
                  <TrashIcon className='h-5 w-5' />
                )}
              </button>
            </div>
          </td>
        </tr>

        {/* Document Rows */}
        {openFolders[folder.id] && (
          <>
            {/* Render subfolders first */}
            {sortedSubFolders.map((subfolder) => renderFolderContent(subfolder, depth + 1))}

            {/* Then render folder documents */}
            {sortedDocuments.map((doc) => (
              <tr key={doc.id} className='group hover:bg-gray-50 dark:hover:bg-dark-background-hover'>
                <td className='overflow-hidden px-6 py-4'>
                  <div className='flex min-w-0 items-center'>
                    <div style={{ marginLeft: `${(depth + 2) * 1.5}rem` }} className='flex min-w-0 flex-1 items-center'>
                      <input
                        type='checkbox'
                        checked={selectedDocuments.includes(doc.id) || (docIdsInsideSelectedFolders || []).includes(doc.id)}
                        onChange={(e) => onDocumentSelection && onDocumentSelection(doc.id, e.target.checked)}
                        className='mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-border-dark'
                        disabled={(docIdsInsideSelectedFolders || []).includes(doc.id)}
                        title={(docIdsInsideSelectedFolders || []).includes(doc.id) ? t('content.documents.folderTree.tooltips.selectedViaFolder') : t('content.documents.folderTree.tooltips.selectDocument')}
                      />
                      <DocumentIcon className='mr-2 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-dark-text-secondary' aria-hidden='true' />
                      <div className='min-w-0 flex-1'>
                        <div className='truncate text-sm font-medium text-gray-900 group-hover:line-clamp-2 group-hover:text-clip group-hover:whitespace-normal dark:text-dark-text'>
                          {highlight(doc.title) as any}
                        </div>
                        <div className='text-sm text-gray-500 dark:text-dark-text-secondary'>{getExtension(doc.title)}</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-dark-text-secondary'>{formatDate(doc.createdAt)}</td>
                <td className='whitespace-nowrap px-4 py-4 text-sm text-gray-500 dark:text-dark-text-secondary'>{formatFileSize(doc.fileSize)}</td>
                <td className='whitespace-nowrap px-4 py-4'>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      doc.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : doc.status === 'PROCESSING'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : doc.status === 'ERROR'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'
                    }`}
                  >
                    {t(`content.documents.status.${(doc.status || 'PENDING').toLowerCase()}`)}
                  </span>
                </td>
                <td className='whitespace-nowrap px-4 py-4 text-right text-sm font-medium'>
                  <div className='flex items-center justify-end space-x-2'>
                    <button
                      onClick={() => {
                        setCurrentRecord(doc);
                        showRenameModal();
                      }}
                      className='text-primary hover:text-primary-hover dark:text-primary-light dark:hover:text-primary'
                      title={t('content.documents.folderTree.actions.renameDocument')}
                    >
                      <PencilIcon className='h-5 w-5' />
                    </button>
                    <button
                      onClick={() => handleRemoveFile([doc.id])}
                      className='text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:text-red-300'
                      disabled={deletingDocumentIds.includes(doc.id)}
                      title={t('content.documents.folderTree.actions.deleteDocument')}
                    >
                      {deletingDocumentIds.includes(doc.id) ? (
                        <svg className='h-5 w-5 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                      ) : (
                        <TrashIcon className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className='h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-border-dark'>
      <div className='scrollbar-thin max-h-full overflow-auto'>
        <table className='w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700'>
          <thead className='sticky top-0 z-10 bg-gray-50 shadow-sm dark:bg-dark-background-tertiary'>
            <tr>
              <th scope='col' className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.folderTree.columns.name')}
              </th>
              <th scope='col' className='w-44 whitespace-nowrap px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.folderTree.columns.createdDate')}
              </th>
              <th scope='col' className='w-28 whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.folderTree.columns.size')}
              </th>
              <th scope='col' className='w-32 whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.folderTree.columns.status')}
              </th>
              <th scope='col' className='w-24 px-4 py-3'>
                <span className='sr-only'>{t('content.documents.folderTree.columns.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-dark-background-secondary'>
            {[...folderTree].sort((a, b) => sortFactor * compareByName(a.name, b.name)).map((folder) => renderFolderContent(folder))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FolderTreeView;